import * as qs from 'qs-esm'
import type { Where } from 'payload'

import type { Locale } from '@/lib/i18n/config'

export const POSTS_PAGE_LIMIT = 9

export const BLOG_POST_SELECT = {
  id: true,
  title: true,
  slug: true,
  categories: true,
  heroImage: true,
  shortDescription: true,
  meta: true,
  content: true,
} as const

type PostsQueryOptions = {
  categoryId?: number | string | null
  excludeId?: number | string | null
  locale: Locale
  page: number
  q?: string | null
}

function normalizeSearchQuery(q?: string | null): string | null {
  const trimmed = q?.trim()
  return trimmed ? trimmed : null
}

export function buildPostsWhere({
  categoryId,
  excludeId,
  q,
}: Pick<PostsQueryOptions, 'categoryId' | 'excludeId' | 'q'>): Where {
  const conditions: Where[] = [{ _status: { equals: 'published' } }]

  if (excludeId != null) {
    conditions.push({ id: { not_equals: excludeId } })
  }

  if (categoryId != null) {
    conditions.push({ categories: { equals: categoryId } })
  }

  const searchQuery = normalizeSearchQuery(q)
  if (searchQuery) {
    conditions.push({
      or: [
        { title: { contains: searchQuery } },
        { shortDescription: { contains: searchQuery } },
      ],
    })
  }

  return { and: conditions }
}

export function buildPostsFindArgs({ categoryId, excludeId, locale, page, q }: PostsQueryOptions) {
  return {
    collection: 'posts' as const,
    depth: 1,
    limit: POSTS_PAGE_LIMIT,
    locale,
    overrideAccess: false,
    page,
    select: BLOG_POST_SELECT,
    sort: '-publishedAt' as const,
    where: buildPostsWhere({ categoryId, excludeId, q }),
  }
}

export function buildPostsQueryParams({ categoryId, excludeId, locale, page, q }: PostsQueryOptions) {
  return {
    depth: 1,
    limit: POSTS_PAGE_LIMIT,
    locale,
    page,
    select: BLOG_POST_SELECT,
    sort: '-publishedAt',
    where: buildPostsWhere({ categoryId, excludeId, q }),
  }
}

export function buildPostsQueryString(options: PostsQueryOptions): string {
  return qs.stringify(buildPostsQueryParams(options), { addQueryPrefix: false })
}

export type PostsPageResponse<T> = {
  docs: T[]
  hasNextPage: boolean
  page: number
  totalPages: number
}
